import React, { createContext, useContext, useMemo } from 'react';
import { EligibilityService, FeedbackService } from '@honeybook/engagement';
import { useHttpClient } from '@honeybook/core-react';
import { IHttpClient } from '@honeybook/core';

interface EngagementContextValue {
    eligibilityService: EligibilityService;
    feedbackService: FeedbackService;
}

export interface EngagementProviderProps {
    children: React.ReactNode;
}

const createEngagementContextValue = ({
    httpClient
}: {
    httpClient: IHttpClient;
}) => ({
    eligibilityService: new EligibilityService(httpClient),
    feedbackService: new FeedbackService(httpClient)
});

const EngagementContext = createContext<EngagementContextValue>(null);

export const EngagementProvider: React.FC<EngagementProviderProps> = ({
    children
}) => {
    const httpClient = useHttpClient();
    const contextValue = useMemo(
        () => createEngagementContextValue({ httpClient }),
        [httpClient]
    );

    return (
        <EngagementContext.Provider value={contextValue}>
            {children}
        </EngagementContext.Provider>
    );
};

export const useEngagementContext = (): EngagementContextValue => {
    const context = useContext(EngagementContext);

    if (!context) {
        throw new Error(
            'useEngagementContext must be used within an EngagementProvider'
        );
    }

    return context;
};
